/**
 * Table styled components
 */

import styled from "styled-components/macro";
import {
  TableContainer,
  TableCell,
  TableRow,
  Box,
  Typography,
  LinearProgress,
} from "@material-ui/core";

export const SummaryTableContainer = styled(TableContainer)`
  max-height: 600px;
  overflow: auto;
`;

export const StyledTableCell = styled(TableCell)`
  padding: ${(props) => props.theme.spacing(1)}px;
  font-size: 0.75rem;
  white-space: nowrap;
  position: relative;
  z-index: 1;
`;

export const StickyFirstCell = styled(StyledTableCell)`
  position: sticky;
  left: 0;
  z-index: 100;
  background-color: ${(props) => props.theme.palette.background.paper};
`;

export const StickySecondCell = styled(StyledTableCell)`
  position: sticky;
  left: 80px;
  z-index: 100;
  background-color: ${(props) => props.theme.palette.background.paper};
`;

export const HeaderCell = styled(StyledTableCell)`
  background-color: ${(props) => props.theme.palette.background.default};
  font-weight: 600;
  position: sticky;
  top: 0;
  z-index: 101;
`;

export const StickyHeaderFirstCell = styled(HeaderCell)`
  left: 0;
  z-index: 102;
`;

export const StickyHeaderSecondCell = styled(HeaderCell)`
  left: 80px;
  z-index: 102;
`;

export const TotalsRow = styled(TableRow)`
  position: sticky;
  bottom: 0;
  z-index: 101;
  & td {
    font-weight: 600;
    background-color: ${(props) =>
      props.theme.palette.type === "dark"
        ? props.theme.palette.grey[900]
        : props.theme.palette.grey[200]} !important;
  }
`;

export const DataRow = styled(TableRow)`
  & td {
    background-color: ${(props) => props.theme.palette.background.paper};
  }
  &:hover td {
    background-color: ${(props) =>
      props.theme.palette.type === "dark"
        ? props.theme.palette.grey[800]
        : props.theme.palette.grey[100]};
  }
`;

export const GroupRow = styled(TableRow)`
  cursor: pointer;
  & td {
    background-color: ${(props) =>
      props.theme.palette.background.paper} !important;
  }
  &:hover td {
    background-color: ${(props) =>
      props.theme.palette.type === "dark"
        ? props.theme.palette.grey[800]
        : props.theme.palette.grey[100]} !important;
  }
`;

export const ChildRow = styled(TableRow)`
  cursor: pointer;
  & td {
    background-color: ${(props) =>
      props.theme.palette.background.default} !important;
  }
  &:hover td {
    background-color: ${(props) =>
      props.theme.palette.type === "dark"
        ? props.theme.palette.grey[700]
        : props.theme.palette.grey[200]} !important;
  }
`;

export const GrandchildRow = styled(TableRow)`
  & td {
    background-color: ${(props) =>
      props.theme.palette.type === "dark"
        ? props.theme.palette.grey[800]
        : props.theme.palette.grey[100]} !important;
  }
`;

export const IndentedCell = styled(StickyFirstCell)`
  padding-left: ${(props) => props.theme.spacing(props.$indent || 1) * 2}px;
`;

export const RemainingCell = styled(StyledTableCell)`
  color: ${(props) =>
    props.$negative ? props.theme.palette.error.main : "inherit"};
  font-weight: ${(props) => (props.$negative ? 600 : 400)};
`;

// Percent Bar styles
export const PercentBarContainer = styled(Box)`
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 100px;
`;

export const PercentValue = styled(Typography)`
  font-size: 0.75rem;
  font-weight: 600;
  min-width: 55px;
  text-align: right;
`;

export const StyledLinearProgress = styled(LinearProgress)`
  flex: 1;
  height: 8px;
  border-radius: 4px;
  background-color: ${(props) => props.theme.palette.grey[300]};
  & .MuiLinearProgress-bar {
    border-radius: 4px;
    background-color: ${(props) =>
      props.$status === "danger"
        ? props.theme.palette.error.main
        : props.$status === "warning"
        ? props.theme.palette.warning.main
        : props.theme.palette.success.main};
  }
`;

// Muted year colors for alternating row groups
const YEAR_COLORS_LIGHT = [
  "#f5f9ff", // very light blue
  "#fff9f5", // very light orange/peach
  "#f5fff7", // very light green
  "#fff5fb", // very light pink
  "#fefff5", // very light yellow
  "#f9f5ff", // very light purple
  "#f5ffff", // very light cyan
  "#fff5f5", // very light red
];

const YEAR_COLORS_DARK = [
  "#1a2333", // dark blue tint
  "#2a2318", // dark orange tint
  "#1a2a1d", // dark green tint
  "#2a1a25", // dark pink tint
  "#2a2a18", // dark yellow tint
  "#221a2a", // dark purple tint
  "#1a2a2a", // dark cyan tint
  "#2a1a1a", // dark red tint
];

// Darker header colors (more saturated versions of row colors)
const YEAR_HEADER_COLORS_LIGHT = [
  "#d0e3ff", // blue
  "#ffe4d0", // orange/peach
  "#d0f5d8", // green
  "#ffd0eb", // pink
  "#f5f5d0", // yellow
  "#e8d0ff", // purple
  "#d0f5f5", // cyan
  "#ffd0d0", // red
];

const YEAR_HEADER_COLORS_DARK = [
  "#2a3d5c", // dark blue
  "#4a3d28", // dark orange
  "#2a4a32", // dark green
  "#4a2a3d", // dark pink
  "#4a4a28", // dark yellow
  "#3d2a4a", // dark purple
  "#2a4a4a", // dark cyan
  "#4a2a2a", // dark red
];

export const getYearColor = (yearIndex, isDark) => {
  const colors = isDark ? YEAR_COLORS_DARK : YEAR_COLORS_LIGHT;
  return colors[yearIndex % colors.length];
};

export const getYearHeaderColor = (yearIndex, isDark) => {
  const colors = isDark ? YEAR_HEADER_COLORS_DARK : YEAR_HEADER_COLORS_LIGHT;
  return colors[yearIndex % colors.length];
};

export const YearColoredWellRow = styled(TableRow)`
  & td {
    background-color: ${(props) =>
      getYearColor(
        props.$yearIndex || 0,
        props.theme.palette.type === "dark"
      )} !important;
  }
  &:hover td {
    background-color: ${(props) =>
      props.theme.palette.type === "dark"
        ? props.theme.palette.grey[700]
        : props.theme.palette.grey[200]} !important;
  }
`;

export const YearColoredStickyCell = styled(StyledTableCell)`
  position: sticky;
  left: 0;
  z-index: 100;
  background-color: ${(props) =>
    getYearColor(
      props.$yearIndex || 0,
      props.theme.palette.type === "dark"
    )} !important;
`;

export const YearColoredSecondStickyCell = styled(StyledTableCell)`
  position: sticky;
  left: 80px;
  z-index: 100;
  background-color: ${(props) =>
    getYearColor(
      props.$yearIndex || 0,
      props.theme.palette.type === "dark"
    )} !important;
`;

// Year-colored header row (darker version of row color)
export const YearColoredGroupHeader = styled(TableRow)`
  & td {
    font-weight: 600;
    background-color: ${(props) =>
      getYearHeaderColor(
        props.$yearIndex || 0,
        props.theme.palette.type === "dark"
      )} !important;
  }
`;

export const YearColoredHeaderStickyCell = styled(StyledTableCell)`
  position: sticky;
  left: 0;
  z-index: 100;
  font-weight: 600;
  background-color: ${(props) =>
    getYearHeaderColor(
      props.$yearIndex || 0,
      props.theme.palette.type === "dark"
    )} !important;
`;

export const YearColoredHeaderSecondStickyCell = styled(StyledTableCell)`
  position: sticky;
  left: 80px;
  z-index: 100;
  font-weight: 600;
  background-color: ${(props) =>
    getYearHeaderColor(
      props.$yearIndex || 0,
      props.theme.palette.type === "dark"
    )} !important;
`;
